/* eslint-disable @typescript-eslint/no-explicit-any */

import io, { Socket } from "socket.io-client";

const API_BASE_URL =
  import.meta.env.VITE_BACKEND_URL || "http://localhost:8080";

export class SocketIOWebSocketService {
  private socket: typeof Socket | null = null;
  private listeners: Map<string, ((data: any) => void)[]> = new Map();
  private connectionPromise: Promise<void> | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private authState: any = null;

  constructor() {
    this.readAuthState();
  }

  /**
   * Re-reads authState from localStorage.
   *
   * This service is a module-level singleton, so the constructor runs on first
   * import — before the user has logged in. Reading auth once at construction
   * left `authState` null for the whole session on a fresh login, and the
   * socket then connected as `?userId=undefined`, which the backend accepts
   * (the query value arrives as the *string* "undefined", which is truthy).
   * Every client therefore registered under one bogus user id. Read at connect
   * time instead.
   */
  private readAuthState(): void {
    const authStateStr = localStorage.getItem("authState");
    if (!authStateStr) {
      this.authState = null;
      return;
    }
    try {
      this.authState = JSON.parse(authStateStr);
    } catch (error) {
      console.error("Failed to parse authState:", error);
      this.authState = null;
    }
  }

  connect(): Promise<void> {
    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    if (this.socket?.connected) {
      return Promise.resolve();
    }

    // Auth may have arrived after this singleton was constructed.
    this.readAuthState();

    const userId = this.authState?.user?.id;
    if (!userId) {
      // Connecting anyway would register the socket as the literal string
      // "undefined" server-side and corrupt the live-user counts.
      return Promise.reject(
        new Error("Cannot open notification socket: no authenticated user")
      );
    }

    this.connectionPromise = new Promise((resolve, reject) => {
      const wsUrl = API_BASE_URL;

      // Disconnect existing socket if any
      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
      }

      // Connect to the 'notifications' namespace that the backend Gateway uses
      const socketUrl = `${wsUrl}/notifications`;

      this.socket = io(socketUrl, {
        transports: ["websocket", "polling"],
        auth: {
          userId: this.authState?.user?.id,
          token: this.authState?.token,
          user: this.authState?.user,
        },
        query: {
          userId: this.authState?.user?.id,
        },
        forceNew: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      this.socket.on("connect", () => {
        this.reconnectAttempts = 0;
        this.connectionPromise = null;

        // Register with backend user tracking system
        if (this.authState?.user?.id) {
          // Try multiple registration approaches
          const userId = this.authState.user.id;

          // Approach 1: Direct service call format
          this.socket?.emit("user-connect", {
            userId: userId,
            timestamp: new Date().toISOString(),
          });

          // Approach 2: Join room format
          this.socket?.emit("join-user-room", { userId: userId });

          // Approach 3: Authentication format
          this.socket?.emit("authenticate", {
            userId: userId,
            user: this.authState.user,
          });

          // Approach 4: Try calling the backend service method directly
          this.socket?.emit("register-user", {
            userId: userId,
            socketId: this.socket?.id,
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
          });

          // Approach 5: Try the exact format that backend WebSocketService expects
          this.socket?.emit("handle-user-connection", {
            userId: userId,
            socket: { id: this.socket?.id },
          });
        } else {
          console.warn(
            "No user ID found in auth state, cannot register with backend"
          );
        }

        this.notifyListeners("CONNECTION", { connected: true });
        resolve();
      });

      this.socket.on("disconnect", (reason: any) => {
        this.connectionPromise = null;

        this.notifyListeners("CONNECTION", { connected: false, reason });
      });

      this.socket.on("connect_error", (error: any) => {
        console.error("Socket.IO connection error:", error);
        this.connectionPromise = null;
        if (this.reconnectAttempts === 0) {
          reject(error);
        }
        this.reconnectAttempts++;
      });

      // Listen for our custom message events
      this.socket.on("message", (data: any) => {
        this.handleMessage(data);
      });

      // Listen for direct notification events (legacy compatibility)
      this.socket.on("notification", (data: any) => {
        this.notifyListeners("NOTIFICATION", data);
      });

      // Listen for new notification events
      this.socket.on("new-notification", (data: any) => {
        this.notifyListeners("NOTIFICATION", data);
      });

      // Listen for user-specific notifications
      this.socket.on("user-notification", (data: any) => {
        this.notifyListeners("NOTIFICATION", data);
      });

      // Connection confirmation
      this.socket.on("connected", (data: any) => {
        this.notifyListeners("CONNECTION", { connected: true, ...data });
      });

      // Error events
      this.socket.on("error", (data: any) => {
        console.error("Socket.IO error event:", data);
        this.notifyListeners("ERROR", data);
      });

      // Listen for any other events that might be sent by backend
      (this.socket as any).onAny?.((eventName: string, ...args: any[]) => {
        if (
          eventName.includes("notification") ||
          eventName.includes("message")
        ) {
          this.handleMessage({ type: eventName, data: args[0] });
        }
      });

      // Also try to listen for backend-specific events that might indicate successful registration
      this.socket.on("user-registered", (data: any) => {
        this.notifyListeners("CONNECTION", {
          connected: true,
          registered: true,
          ...data,
        });
      });

      this.socket.on("registration-failed", (data: any) => {
        console.error("User registration failed:", data);
        this.notifyListeners("ERROR", { type: "REGISTRATION_FAILED", ...data });
      });

      // Set connection timeout
      setTimeout(() => {
        if (!this.socket?.connected) {
          this.connectionPromise = null;
          reject(new Error("Connection timeout"));
        }
      }, 10000); // 10 seconds timeout
    });

    return this.connectionPromise;
  }

  private handleMessage(message: any) {
    // Handle standardized message format: { type: "NOTIFICATION", data: {...} }
    if (message.type && message.data !== undefined) {
      this.notifyListeners(message.type, message.data);
      return;
    }

    // Handle direct notification data
    if (message.subject || message.id) {
      this.notifyListeners("NOTIFICATION", message);
      return;
    }
  }

  private notifyListeners(eventType: string, data: any) {
    const listeners = this.listeners.get(eventType) || [];

    listeners.forEach((listener) => {
      try {
        listener(data);
      } catch (error) {
        console.error(`Error in ${eventType} listener:`, error);
      }
    });
  }

  subscribe(eventType: string, callback: (data: any) => void): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    this.listeners.get(eventType)!.push(callback);

    return () => {
      const listeners = this.listeners.get(eventType);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    };
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.listeners.clear();
    this.reconnectAttempts = 0;
    this.connectionPromise = null;
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  // Send a test message (for debugging)
  sendTestMessage(message: any): void {
    if (this.socket?.connected) {
      this.socket.emit("test-message", message);
    } else {
      console.error("Socket not connected");
    }
  }

  // Get connection status for debugging
  getStatus() {
    return {
      connected: this.isConnected(),
      connecting: this.socket?.connect || false,
      reconnectAttempts: this.reconnectAttempts,
      socketId: this.socket?.id,
      listenersCount: Array.from(this.listeners.entries()).reduce(
        (acc, [type, listeners]) => {
          acc[type] = listeners.length;
          return acc;
        },
        {} as Record<string, number>
      ),
    };
  }
}
